import LanguageIcon from "@mui/icons-material/Language";
import { Divider, Grid, Typography } from "@mui/material";
import { fetchCustomerBranding } from "actions/brandingActions";
import YourEmployeesIcon from "assets/IronstoneProductLogos/YourEmployees";
import { YourITSystems } from "assets/IronstoneProductLogos/YourITSystems";
import clsx from "clsx";
import { CountryFlag } from "components/Common/CountrySelector";
import { IronstoneProductStatus } from "components/Common/IronstoneProductStatus";
import { selectCustomerBranding } from "features/branding";
import { useApiOnce, useAppSelector } from "hooks/hooks";
import { CustomerInfo } from "types";
import { IRONSTONE_PRODUCT_DISPLAY_NAMES } from "utilities/constants/constants";
import { tripleDotTruncateString } from "utilities/stringFormattingUtility";
import styles from "./CompanyInfoSection.module.scss";

interface CompanyInfoSectionProps {
	customerInfo: CustomerInfo;
}

export const CompanyInfoSection = ({ customerInfo }: CompanyInfoSectionProps) => {
	useApiOnce(fetchCustomerBranding, useAppSelector(selectCustomerBranding));

	const { branding } = useAppSelector(selectCustomerBranding);

	const urlPresent = customerInfo?.companyUrl !== "" || customerInfo?.companyUrl;

	return (
		<Grid container className={styles.container}>
			<Grid className={styles.companyInfo}>
				<Grid
					container
					className={clsx({
						[styles.logoContainer]: branding?.logoBase64,
						[styles.hide]: !branding?.logoBase64,
					})}
				>
					<img
						src={`data:image/png;base64,${branding?.brandingBase64}`}
						alt="Company Logo"
						className={styles.logo}
					/>
				</Grid>
				<Grid container className={styles.details}>
					<Typography variant="h2" fontWeight={500}>
						{customerInfo?.departmentName}
					</Typography>
					<Grid container className={styles.locationInfo}>
						<CountryFlag country={customerInfo?.location} coloredBackground={false} />
						<Typography variant="body1">{customerInfo?.location}</Typography>
					</Grid>
					<Grid
						container
						className={clsx({
							[styles.urlInfo]: urlPresent,
							[styles.hide]: !urlPresent,
						})}
					>
						<LanguageIcon className={styles.languageIcon} />
						<Typography
							variant="description"
							fontWeight={500}
							className={styles.url}
							onClick={() => window.open(customerInfo?.companyUrl)}
						>
							{customerInfo?.companyUrl}
						</Typography>
					</Grid>
				</Grid>
			</Grid>

			<Divider orientation="vertical" flexItem className={styles.divider} />
			<Grid className={styles.companyInfoProducts}>
				<Grid item>
					<Grid item xs={12} mb={2}>
						<Typography variant="body1">Products</Typography>
					</Grid>
					<Grid container gap={1}>
						<IronstoneProductStatus
							icon={<YourEmployeesIcon active={customerInfo?.yourEmployeesEnabled} />}
							label="IT for your employees"
							active={customerInfo?.yourEmployeesEnabled}
						/>
						<IronstoneProductStatus
							// Make sure that the new it systems logo acutally turns yellow once the product is active
							icon={<YourITSystems active={customerInfo?.yourItSystemsEnabled} />}
							label={IRONSTONE_PRODUCT_DISPLAY_NAMES.YOUR_IT_SYSTEMS}
							active={customerInfo?.yourItSystemsEnabled}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Divider orientation="vertical" flexItem className={styles.divider} />
			<Grid className={styles.companyInfoContacts}>
				<Typography variant="body1">Ironstone contacts</Typography>
				<Grid container item className={styles.contacts}>
					<Grid container item className={styles.contactInfo}>
						<Typography variant="body1" className={styles.contactType}>
							Primary
						</Typography>
						<Typography variant="body1" className={styles.contactName}>
							{customerInfo.commercialLead}
						</Typography>
					</Grid>
					<Grid container item className={styles.contactInfo}>
						<Typography variant="body1" className={styles.contactType}>
							Technical
						</Typography>
						<Typography variant="body1" className={styles.contactName}>
							{tripleDotTruncateString(customerInfo.tam, 20)}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
